<template>
    <v-row no-gutters class="fill-height">
        <v-col cols="12">
            <!-- New Header with Background -->
            <v-sheet 
                color="primary" 
                class="width-100 pa-6 d-flex flex-column align-center justify-center rounded-lg mb-6 flex-shrink-0"
                style="background: linear-gradient(135deg, var(--v-primary-base) 0%, var(--v-primary-darken2) 100%); width: 100%;"
                elevation="4"
            >
                <h1 id="tour-notifications-title" class="text-h4 font-weight-bold white--text mb-2">
                    <v-icon large color="white" class="mr-2">mdi-bell-ring</v-icon>
                    Central de Notificações
                </h1>
                <div class="white--text text-subtitle-1 opacity-80 mb-4">
                    Gerencie e envie comunicações para seus clientes
                </div>

                <!-- Segmented Control for Main Navigation -->
                <v-btn-toggle
                    id="tour-tabs"
                    v-model="tab"
                    mandatory
                    rounded
                    class="elevation-2"
                    background-color="white"
                    color="primary"
                >
                    <v-btn :value="0" class="px-6 text-capitalize">
                        <v-icon left small>mdi-send</v-icon>
                        Enviar
                    </v-btn>
                    <v-btn 
                        id="tour-settings-tab"
                        :value="1" 
                        :disabled="!isPro && !isSysAdmin"
                        class="px-6 text-capitalize"
                    >
                        <v-icon left small>mdi-cog</v-icon>
                        Configurações
                        <v-chip v-if="!isPro && !isSysAdmin" x-small color="error" class="ml-2 white--text">Pro</v-chip>
                    </v-btn>
                </v-btn-toggle>
            </v-sheet>

            <!-- Content Area -->
            <div class="width-100 flex-grow-1" style="width: 100%;">
                <v-tabs-items v-model="tab" class="transparent">
                    <!-- Tab 1: Send Notifications -->
                    <v-tab-item :value="0">
                        <v-row class="fill-height">
                            <!-- Column 1: Recipients (Data Table) -->
                            <v-col cols="12" md="4" lg="3" class="d-flex flex-column">
                                <v-card id="tour-recipients-card" class="rounded-lg flex-grow-1" elevation="2">
                                    <v-card-title class="pb-2 text-subtitle-1 font-weight-bold grey lighten-5">
                                        <v-icon left color="primary">mdi-account-group</v-icon>
                                        Destinatários
                                        <v-spacer></v-spacer>
                                        <v-chip x-small label color="primary" outlined>
                                            {{ selectedUsers.length }}
                                        </v-chip>
                                    </v-card-title>
                                    <v-divider></v-divider>
                                    
                                    <v-card-text class="pt-2 px-0 flex-grow-1">
                                        <v-text-field
                                            v-model="search"
                                            append-icon="mdi-magnify"
                                            label="Buscar cliente"
                                            single-line
                                            hide-details
                                            dense
                                            outlined
                                            class="mx-4 mb-2 rounded-lg"
                                            color="primary"
                                        ></v-text-field>

                                        <!-- Status filters -->
                                        <div class="mx-4 mb-3">
                                            <div class="text-caption grey--text text--darken-1 mb-1 font-weight-bold text-uppercase">Status do Cliente</div>
                                            <div class="d-flex flex-wrap" style="gap: 6px;">
                                                <v-chip
                                                    v-for="s in customerStatusOptions"
                                                    :key="s.value"
                                                    small
                                                    label
                                                    :color="statusFilters.includes(s.value) ? s.color : 'grey lighten-2'"
                                                    :text-color="statusFilters.includes(s.value) ? 'white' : 'grey darken-1'"
                                                    @click="toggleStatusFilter(s.value)"
                                                    style="cursor: pointer;"
                                                >
                                                    <v-icon x-small left>{{ statusFilters.includes(s.value) ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
                                                    {{ s.label }}
                                                </v-chip>
                                            </div>
                                            <div class="d-flex flex-wrap mt-2" style="gap: 6px;">
                                                <v-chip
                                                    small
                                                    label
                                                    :color="filterOnlyWithContracts ? 'primary' : 'grey lighten-2'"
                                                    :text-color="filterOnlyWithContracts ? 'white' : 'grey darken-1'"
                                                    @click="filterOnlyWithContracts = !filterOnlyWithContracts"
                                                    style="cursor: pointer;"
                                                >
                                                    <v-icon x-small left>{{ filterOnlyWithContracts ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
                                                    Com contrato ativo
                                                </v-chip>
                                            </div>
                                        </div>

                                        <v-data-table
                                            v-model="selectedUsers"
                                            :headers="userHeaders"
                                            :items="filteredUsers"
                                            :search="search"
                                            show-select
                                            item-key="id" 
                                            fixed-header
                                            height="calc(100vh - 470px)"
                                            class="elevation-0"
                                            dense
                                            :footer-props="{
                                                'items-per-page-options': [10, 50, 100, -1],
                                                'items-per-page-text': ''
                                            }"
                                        >
                                            <template v-slot:[`item.name`]="{ item }">
                                                <div class="font-weight-medium text-truncate" style="max-width: 150px;">
                                                    {{ item.name }}
                                                </div>
                                            </template>
                                        </v-data-table>
                                    </v-card-text>
                                </v-card>
                            </v-col>

                            <!-- Column 2: Compose Form -->
                            <v-col cols="12" md="4" lg="5">
                                <v-card class="rounded-lg" elevation="2">
                                    <v-card-title class="grey lighten-5 py-3">
                                        <v-icon left color="primary">mdi-pencil</v-icon>
                                        Nova Notificação
                                    </v-card-title>
                                    <v-divider></v-divider>

                                    <v-card-text class="pt-4">
                                        <!-- WhatsApp Connection Status -->
                                        <v-alert
                                            v-if="!whatsappConnected"
                                            type="warning"
                                            text
                                            dense
                                            class="mb-4 rounded-lg"
                                            icon="mdi-whatsapp"
                                            border="left"
                                        >
                                            Conecte o WhatsApp nas configurações para enviar mensagens.
                                        </v-alert>
                                        
                                        <!-- Variables Chips -->
                                        <div id="tour-variables-alert" class="mb-4">
                                            <div class="text-caption font-weight-bold grey--text text--darken-1 mb-2">
                                                <v-icon x-small left>mdi-variable</v-icon>
                                                VARIÁVEIS (Clique para inserir)
                                            </div>
                                            <div class="d-flex flex-wrap" style="gap: 8px;">
                                                <v-chip
                                                    v-for="v in availableVariables"
                                                    :key="v.key"
                                                    small
                                                    label
                                                    color="primary"
                                                    outlined
                                                    @click="insertVariable(v.key)"
                                                    style="cursor: pointer"
                                                >
                                                    <strong>{{ v.label }}</strong>
                                                </v-chip>
                                            </div>
                                        </div>

                                        <!-- Channel Selection (Toggle Buttons) -->
                                        <div class="mb-4">
                                            <div class="text-caption font-weight-bold mb-1 ml-1 text-uppercase grey--text">Canal de Envio</div>
                                            <v-btn-toggle
                                                id="tour-channel-selection"
                                                v-model="channel"
                                                mandatory
                                                dense
                                                borderless
                                                color="primary"
                                                class="d-flex width-100"
                                                style="width: 100%"
                                            >
                                                <v-btn value="app" class="flex-grow-1" outlined>
                                                    <v-icon left small>mdi-cellphone</v-icon>
                                                    App
                                                </v-btn>
                                                <v-btn 
                                                    value="whatsapp" 
                                                    class="flex-grow-1" 
                                                    outlined
                                                    :disabled="!whatsappConnected"
                                                >
                                                    <v-icon left small>mdi-whatsapp</v-icon>
                                                    WhatsApp
                                                </v-btn>
                                                <v-btn 
                                                    value="both" 
                                                    class="flex-grow-1" 
                                                    outlined
                                                    :disabled="!whatsappConnected"
                                                >
                                                    <v-icon left small>mdi-check-all</v-icon>
                                                    Ambos
                                                </v-btn>
                                            </v-btn-toggle>
                                        </div>

                                        <v-form id="tour-notification-form" ref="form" v-model="valid">
                                            <v-text-field
                                                v-model="notification.title"
                                                label="Título"
                                                :rules="[v => !!v || 'Obrigatório']"
                                                outlined
                                                dense
                                                required
                                                class="rounded-lg"
                                                @focus="onFocus('title')"
                                            ></v-text-field>

                                            <v-textarea
                                                v-model="notification.body"
                                                label="Mensagem"
                                                :rules="[v => !!v || 'Obrigatória']"
                                                outlined
                                                rows="5"
                                                required
                                                auto-grow
                                                class="rounded-lg"
                                                counter
                                                @focus="onFocus('body')"
                                            ></v-textarea>>

                                            <v-select
                                                v-model="notification.screen"
                                                :items="screens"
                                                label="Destino ao clicar"
                                                outlined
                                                dense
                                                clearable
                                                prepend-inner-icon="mdi-link"
                                                class="rounded-lg"
                                            ></v-select>

                                            <!-- Media Upload for WhatsApp -->
                                            <div v-if="channel === 'whatsapp' || channel === 'both'">
                                                <v-divider class="my-4"></v-divider>
                                                <div class="text-caption font-weight-bold mb-2 d-flex align-center grey--text text--darken-1">
                                                    <v-icon small class="mr-1">mdi-paperclip</v-icon> 
                                                    MÍDIA (WHATSAPP)
                                                </div>
                                                <v-row dense>
                                                    <v-col cols="4">
                                                        <v-select
                                                            v-model="mediaType"
                                                            :items="mediaTypes"
                                                            label="Tipo"
                                                            outlined
                                                            dense
                                                            hide-details
                                                            class="rounded-lg"
                                                        ></v-select>
                                                    </v-col>
                                                    <v-col cols="8">
                                                        <v-text-field
                                                            v-if="mediaType"
                                                            v-model="mediaUrl"
                                                            label="URL do Arquivo"
                                                            outlined
                                                            dense
                                                            hide-details
                                                            placeholder="https://..."
                                                            class="rounded-lg"
                                                        ></v-text-field>
                                                    </v-col>
                                                </v-row>
                                            </div>
                                        </v-form>
                                    </v-card-text>
                                    <v-card-actions class="pa-4 grey lighten-5">
                                        <v-btn
                                            id="tour-send-button"
                                            block
                                            color="primary"
                                            large
                                            elevation="2"
                                            :loading="loading"
                                            :disabled="!valid || selectedUsers.length === 0"
                                            @click="sendNotification"
                                            class="font-weight-bold"
                                        >
                                            Enviar Notificação 
                                            <v-chip
                                                v-if="selectedUsers.length > 0"
                                                x-small
                                                class="ml-2"
                                                color="white"
                                                text-color="primary"
                                            >
                                                {{ selectedUsers.length }}
                                            </v-chip>
                                            <v-icon right>mdi-send</v-icon>
                                        </v-btn>
                                    </v-card-actions>
                                </v-card>
                            </v-col>

                            <!-- Column 3: Sticky Preview -->
                            <v-col cols="12" md="4" lg="4">
                                <v-card class="rounded-lg" elevation="2">
                                    <v-card-title class="grey lighten-5 py-3">
                                        <v-icon left color="primary">mdi-cellphone</v-icon>
                                        Pré-visualização
                                    </v-card-title>
                                    <v-divider></v-divider>

                                    <v-card-text class="pt-4">
                                    
                                    <div id="tour-preview-section" class="grey lighten-4 rounded-xl pa-4 transition-swing text-center elevation-1">
                                        <div v-if="channel === 'app' || channel === 'both'">
                                            <div class="text-caption grey--text mb-2 text-left" v-if="channel === 'both'">App Mobile</div>
                                            <notification-preview
                                                :title="previewTitle"
                                                :body="previewBody"
                                                type="app"
                                                class="mx-auto"
                                            ></notification-preview>
                                        </div>
                                        
                                        <v-divider v-if="channel === 'both'" class="my-4"></v-divider>

                                        <div v-if="channel === 'whatsapp' || channel === 'both'">
                                            <div class="text-caption grey--text mb-2 text-left" v-if="channel === 'both'">WhatsApp</div>
                                            <notification-preview
                                                :title="previewTitle"
                                                :body="previewBody"
                                                type="whatsapp"
                                                class="mx-auto"
                                            ></notification-preview>
                                        </div>

                                        <div v-if="!notification.title && !notification.body" class="d-flex flex-column align-center justify-center py-8 grey--text text-caption">
                                            <v-icon large color="grey lighten-2" class="mb-2">mdi-cellphone-message</v-icon>
                                            Comece a digitar para ver a prévia
                                        </div>
                                    </div>
                                    </v-card-text>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-tab-item>

                    <!-- Tab 2: Settings (Pro Only) -->
                    <v-tab-item :value="1" v-if="isPro || isSysAdmin">
                        <notification-settings />
                    </v-tab-item>
                </v-tabs-items>
            </div>

            <v-snackbar v-model="snackbar.show" :color="snackbar.color" top right>
                {{ snackbar.text }}
                <template v-slot:action="{ attrs }">
                    <v-btn text v-bind="attrs" @click="snackbar.show = false">
                        Fechar
                    </v-btn>
                </template>
            </v-snackbar>
        </v-col>
    </v-row>
</template>

<script>
import { mapGetters } from 'vuex';
import NotificationService from './services/notifications';
import NotificationPreview from './components/NotificationPreview.vue';
import NotificationSettings from './components/NotificationSettings.vue';

export default {
    name: 'NotificationsPage',
    components: {
        NotificationPreview,
        NotificationSettings
    },
    data: () => ({
        tab: 0,
        valid: false,
        loading: false,
        search: '',
        users: [],
        selectedUsers: [],
        notification: {
            title: '',
            body: '',
            screen: null
        },
        userHeaders: [
            { text: 'Nome', value: 'name', sortable: true },
            // Can add more columns like email or phone if available in the user object
        ],
        screens: [
            { text: 'Início', value: 'Home' },
            { text: 'Agendamentos', value: 'Appointments' },
            { text: 'Perfil', value: 'Profile' },
            { text: 'Contratos', value: 'Contracts' }
        ],
        snackbar: {
            show: false,
            text: '',
            color: 'success'
        },
        // WhatsApp
        channel: 'app',
        whatsappConnected: false,
        whatsappCustomers: [],
        mediaType: null,
        mediaUrl: '',
        mediaTypes: [
            { text: 'Imagem', value: 'image' },
            { text: 'Vídeo', value: 'video' },
            { text: 'Documento', value: 'document' }
        ],
        // Field focus tracking for variable insertion
        focusedField: 'body', // default to body
        availableVariables: [
            { key: 'name', label: 'Nome' },
            { key: 'email', label: 'Email' },
            { key: 'phone', label: 'Telefone' }
        ],
        // Status filters for WhatsApp customer list
        statusFilters: ['A', 'P'],
        filterOnlyWithContracts: false,
        customerStatusOptions: [
            { value: 'A', label: 'Ativo',          color: 'green darken-1' },
            { value: 'P', label: 'Primeiro contato', color: 'blue darken-1' },
            { value: 'I', label: 'Inativo',        color: 'grey darken-1' },
        ]
    }),
    computed: {
        ...mapGetters('auth', ['isPro', 'isSysAdmin']),
        filteredUsers() {
            // Pick base list based on channel
            const baseList = (this.channel === 'app') ? this.users : this.whatsappCustomers;
            // Apply status and contract filters (same for all channels)
            return baseList.filter(c => {
                const statusOk = this.statusFilters.length === 0 || this.statusFilters.includes(c.customer_status);
                const contractOk = !this.filterOnlyWithContracts || (c.active_contracts_count > 0);
                return statusOk && contractOk;
            });
        },
        previewTitle() {
            return this.replaceVariables(this.notification.title);
        },
        previewBody() {
            return this.replaceVariables(this.notification.body);
        }
    },
    async created() {
        await this.checkWhatsappConnection();
        this.fetchUsers();
    },
    mounted() {
        // Expose for tour
        window.$notificationsPage = this;
    },
    beforeDestroy() {
        // Clean up global reference
        if (window.$notificationsPage === this) {
            window.$notificationsPage = null;
        }
    },
    watch: {
        channel(newVal) {
            // Clear selections when switching channels
            this.selectedUsers = [];
            // Reset filters to defaults
            this.statusFilters = ['A', 'P'];
            this.filterOnlyWithContracts = false;
            // Fetch appropriate customer list
            if (newVal === 'whatsapp' || newVal === 'both') {
                this.fetchWhatsappCustomers();
            }
        }
    },
    methods: {
        onFocus(field) {
            this.focusedField = field;
        },
        toggleStatusFilter(value) {
            const idx = this.statusFilters.indexOf(value);
            if (idx === -1) {
                this.statusFilters.push(value);
            } else {
                this.statusFilters.splice(idx, 1);
            }
            // Clear selection when filter changes to avoid stale selections
            this.selectedUsers = [];
        },
        insertVariable(variableKey) {
            const variable = `{{${variableKey}}}`;
            if (this.focusedField === 'title') {
                this.notification.title += ` ${variable}`;
            } else {
                this.notification.body += ` ${variable}`;
            }
        },
        async checkWhatsappConnection() {
            try {
                const response = await NotificationService.checkWhatsappStatus();
                this.whatsappConnected = response.data.status === 'CONNECTED';
            } catch (error) {
                this.whatsappConnected = false;
                console.error('Error checking WhatsApp status:', error);
            }
        },
        async fetchUsers() {
            try {
                const response = await NotificationService.getUsers();
                this.users = response.data;
            } catch (error) {
                this.showSnackbar('Erro ao carregar usuários', 'error');
                console.error(error);
            }
        },
        async fetchWhatsappCustomers() {
            try {
                const response = await NotificationService.getWhatsappCustomers();
                this.whatsappCustomers = response.data;
            } catch (error) {
                this.showSnackbar('Erro ao carregar clientes do WhatsApp', 'error');
                console.error(error);
            }
        },
        async sendNotification() {
            if (!this.$refs.form.validate() || this.selectedUsers.length === 0) return;

            this.loading = true;
            try {
                if (this.channel === 'app') {
                    await this.sendToApp();
                } else if (this.channel === 'whatsapp') {
                    await this.sendToWhatsapp();
                } else if (this.channel === 'both') {
                    await this.sendToBoth();
                }
                this.resetForm();
            } catch (error) {
                this.showSnackbar('Erro ao enviar notificação', 'error');
                console.error(error);
            } finally {
                this.loading = false;
            }
        },
        async sendToApp() {
            const payload = {
                // selectedUsers is now an array of objects, map to IDs
                users: this.selectedUsers.map(u => u.id),
                title: this.notification.title,
                body: this.notification.body,
                screen: this.notification.screen
            };
            const response = await NotificationService.sendNotification(payload);
            this.showSnackbar(`Notificação enviada com sucesso! (${response.data.success_count} enviados)`, 'success');
        },
        async sendToWhatsapp() {
            // selectedUsers is now an array of objects which should already have phone if it came from whatsappCustomers
            // But if we are in 'app' channel, we might not have phone? 
            // Actually 'channel' determines valid users.
            
            // If channel is 'whatsapp' or 'both', we are selecting from a list that should have phones.
            // Let's being safe:
            const numbers = this.selectedUsers
                .map(user => {
                    // If the user object itself has phone (from whatsappCustomers)
                    if (user.phone) return user.phone;
                    // Fallback: try to find in whatsappCustomers (if we are in 'both' mode and selected from 'app' list?? 
                    // No, invalid assumption. 'both' mode should probably list users that satisfy both or union? 
                    // Current logic: watch 'channel' -> re-fetch/filter users.
                    // If channel is 'both', we fetchWhatsappCustomers().
                    // Wait, existing code: 
                    // if (newVal === 'whatsapp' || newVal === 'both') { this.fetchWhatsappCustomers(); }
                    // So filteredUsers returns whatsappCustomers when 'both'. 
                    // So selected users WILL be from whatsappCustomers list.
                    return user.phone;
                })
                .filter(phone => phone);

            if (numbers.length === 0) {
                this.showSnackbar('Nenhum número de telefone encontrado', 'error');
                return;
            }

            const payload = {
                numbers: numbers,
                title: this.notification.title,
                message: this.notification.body
            };

            // Add media if provided
            if (this.mediaType && this.mediaUrl) {
                payload.media_url = this.mediaUrl;
                payload.media_type = this.mediaType;
            }

            const response = await NotificationService.sendWhatsappMessage(payload);
            const results = response.data.results;
            const successCount = Object.values(results).filter(r => r === 'sent').length;
            this.showSnackbar(`WhatsApp enviado! (${successCount}/${numbers.length} sucesso)`, 'success');
        },
        async sendToBoth() {
            // Send to app
            await this.sendToApp();
            
            // Send to WhatsApp (text only for both)
            const numbers = this.selectedUsers
                .map(u => u.phone)
                .filter(phone => phone);

            if (numbers.length > 0) {
                const payload = {
                    numbers: numbers,
                    title: this.notification.title,
                    message: this.notification.body
                };
                await NotificationService.sendWhatsappMessage(payload);
            }
            
            this.showSnackbar('Notificações enviadas para ambos os canais!', 'success');
        },
        resetForm() {
            this.$refs.form.reset();
            this.selectedUsers = [];
            this.notification.screen = null;
            this.mediaType = null;
            this.mediaUrl = '';
        },
        showSnackbar(text, color) {
            this.snackbar.text = text;
            this.snackbar.color = color;
            this.snackbar.show = true;
        },
        replaceVariables(text) {
            if (!text) return '';
            if (this.selectedUsers.length === 0) return text;

            // Use the first selected user for preview
            const user = this.selectedUsers[0];
            // No need to find by ID anymore
             
            if (!user) return text;

            return text
                .replace(/{{name}}/g, user.name)
                .replace(/{{email}}/g, user.email)
                .replace(/{{phone}}/g, user.phone);
        }
    }
};
</script>
